// =============================================================================
// Scape Ventures - Main JavaScript
// =============================================================================

/**
 * Main entry point for Scape Ventures website scripts
 */

// =============================================================================
// Navigation
// =============================================================================

class Navigation {
    private header: HTMLElement | null;
    private toggle: HTMLElement | null;
    private menu: HTMLElement | null;
    private isOpen: boolean = false;

    constructor() {
        this.header = document.getElementById('site-header');
        this.toggle = document.querySelector('.nav-toggle');
        this.menu = document.querySelector('.nav-menu');

        this.init();
    }

    private init(): void {
        this.bindEvents();
        this.handleScroll();
    }

    private bindEvents(): void {
        // Mobile menu toggle
        if (this.toggle) {
            this.toggle.addEventListener('click', () => this.toggleMenu());
        }

        // Close menu on link click
        const navLinks = document.querySelectorAll('.nav-link');
        navLinks.forEach(link => {
            link.addEventListener('click', () => this.closeMenu());
        });

        // Header scroll effect
        window.addEventListener('scroll', () => this.handleScroll());

        // Close menu on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closeMenu();
            }
        });
    }

    private toggleMenu(): void {
        this.isOpen = !this.isOpen;
        this.menu?.classList.toggle('is-open', this.isOpen);
        this.toggle?.setAttribute('aria-expanded', String(this.isOpen));
        document.body.style.overflow = this.isOpen ? 'hidden' : '';
    }

    private closeMenu(): void {
        this.isOpen = false;
        this.menu?.classList.remove('is-open');
        this.toggle?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
    }

    private handleScroll(): void {
        const scrolled = window.scrollY > 50;
        this.header?.classList.toggle('scrolled', scrolled);
    }
}

// =============================================================================
// Smooth Scroll
// =============================================================================

class SmoothScroll {
    constructor() {
        this.init();
    }

    private init(): void {
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', (e) => this.handleClick(e));
        });
    }

    private handleClick(e: Event): void {
        const href = (e.currentTarget as HTMLAnchorElement).getAttribute('href');
        if (!href || href === '#') return;

        const target = document.querySelector(href);
        if (target) {
            e.preventDefault();
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }
}

// =============================================================================
// Portfolio Filter
// =============================================================================

class PortfolioFilter {
    private filterBtns: NodeListOf<HTMLElement>;
    private items: NodeListOf<HTMLElement>;

    constructor() {
        this.filterBtns = document.querySelectorAll('.filter-btn');
        this.items = document.querySelectorAll('.portfolio-item');

        if (this.filterBtns.length > 0) {
            this.init();
        }
    }

    private init(): void {
        this.filterBtns.forEach(btn => {
            btn.addEventListener('click', () => this.filter(btn));
        });
    }

    private filter(btn: HTMLElement): void {
        const category = btn.dataset.filter;

        // Update active button
        this.filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Filter items
        this.items.forEach(item => {
            const itemCategory = item.dataset.category;
            const show = category === 'all' || itemCategory === category;

            item.style.display = show ? '' : 'none';
            item.style.opacity = show ? '1' : '0';
        });
    }
}

// =============================================================================
// Form Validation
// =============================================================================

class ContactForm {
    private form: HTMLFormElement | null;

    constructor() {
        this.form = document.querySelector('.contact-form');

        if (this.form) {
            this.init();
        }
    }

    private init(): void {
        this.form?.addEventListener('submit', (e) => this.handleSubmit(e));

        // Add input validation listeners
        const inputs = this.form?.querySelectorAll('input, textarea, select');
        inputs?.forEach(input => {
            input.addEventListener('blur', () => this.validateField(input as HTMLInputElement));
            input.addEventListener('input', () => this.clearError(input as HTMLInputElement));
        });
    }

    private handleSubmit(e: Event): void {
        e.preventDefault();

        if (this.validateForm()) {
            // Form is valid - submit or show success message
            console.log('Form submitted successfully');
            // this.form?.submit();
        }
    }

    private validateForm(): boolean {
        const inputs = this.form?.querySelectorAll('[required]');
        let isValid = true;

        inputs?.forEach(input => {
            if (!this.validateField(input as HTMLInputElement)) {
                isValid = false;
            }
        });

        return isValid;
    }

    private validateField(input: HTMLInputElement): boolean {
        const value = input.value.trim();
        let isValid = true;

        if (input.required && !value) {
            isValid = false;
            this.showError(input, 'This field is required');
        } else if (input.type === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
                this.showError(input, 'Please enter a valid email address');
            }
        }

        if (isValid) {
            this.clearError(input);
        }

        return isValid;
    }

    private showError(input: HTMLInputElement, message: string): void {
        input.classList.add('error');

        let errorEl = input.parentElement?.querySelector('.error-message');
        if (!errorEl) {
            errorEl = document.createElement('span');
            errorEl.className = 'error-message';
            input.parentElement?.appendChild(errorEl);
        }
        errorEl.textContent = message;
    }

    private clearError(input: HTMLInputElement): void {
        input.classList.remove('error');
        const errorEl = input.parentElement?.querySelector('.error-message');
        errorEl?.remove();
    }
}

// =============================================================================
// Scroll Animations
// =============================================================================

class ScrollAnimations {
    private observer: IntersectionObserver;

    constructor() {
        this.observer = new IntersectionObserver(
            (entries) => this.handleIntersection(entries),
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        this.init();
    }

    private init(): void {
        // Add animation classes to elements
        const animatedElements = document.querySelectorAll(
            '.section-header, .portfolio-card, .service-card, .value-card, ' +
            '.portfolio-item, .stat-item, .timeline-item, .process-step'
        );

        animatedElements.forEach(el => {
            el.classList.add('animate-on-scroll');
            this.observer.observe(el);
        });
    }

    private handleIntersection(entries: IntersectionObserverEntry[]): void {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('animated');
                this.observer.unobserve(entry.target);
            }
        });
    }
}

// =============================================================================
// FAQ Accordion
// =============================================================================

class FAQAccordion {
    private items: NodeListOf<HTMLDetailsElement>;

    constructor() {
        this.items = document.querySelectorAll('.faq-item');

        if (this.items.length > 0) {
            this.init();
        }
    }

    private init(): void {
        this.items.forEach(item => {
            item.addEventListener('toggle', () => this.handleToggle(item));
        });
    }

    private handleToggle(openedItem: HTMLDetailsElement): void {
        if (openedItem.open) {
            // Close other items
            this.items.forEach(item => {
                if (item !== openedItem && item.open) {
                    item.open = false;
                }
            });
        }
    }
}

// =============================================================================
// Initialize
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {
    new Navigation();
    new SmoothScroll();
    new PortfolioFilter();
    new ContactForm();
    new ScrollAnimations();
    new FAQAccordion();
});

// Export for module usage
export {
    ContactForm, FAQAccordion, Navigation, PortfolioFilter, ScrollAnimations, SmoothScroll
};

